import React, {
  FocusEvent,
  ChangeEvent,
  MouseEvent,
  useEffect,
  useState,
  KeyboardEvent,
  useMemo,
} from 'react';
import styled, { css } from 'styled-components';
import {
  ExclamationTriangleIcon,
  InfoCircleIcon,
  SvgComponent,
} from '../../../assets';
import { theme } from '../../../theme';
import { Alignment } from '../../../types';
import { BodyText, BodySize } from '../../typography';
import { floatLabel } from '../helpers';
import {
  defaultInputHeight,
  FieldStyleProps,
  FieldStyles,
  InputStyleProps,
  InputStyles,
  InputWrapperStyleProps,
  InputWrapperStyles,
} from '../styles';
import { Label as LabelComponent, LabelStyleProps } from '../label';
import { ErrorBox } from '../errorBox';
import { Tooltip } from '../../tooltip';

export type FieldIconSize = 'md' | 'lg';

export type FieldProps = {
  alignUnit?: Alignment;
  errorMessage?: string;
  hasError?: boolean;
  disabled?: boolean;
  label?: string;
  hasWrapperMarginBottom?: boolean;
  iconLeft?: SvgComponent;
  iconRight?: SvgComponent;
  iconSize?: FieldIconSize;
  isCompact?: boolean;
  isFullWidth?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: MouseEvent<HTMLInputElement>) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  size?: BodySize;
  type?: string;
  pattern?: string;
  unit?: string;
  value?: string | number;
  defaultValue?: string | number;
  controlled?: boolean;
  useParentState?: boolean;
  hasTooltip?: boolean;
  toolTipData?: string;
};

type FieldLabelProps = LabelStyleProps & {
  hasLeftIcon: boolean;
  hasLeftUnit: boolean;
};

type IconProps = {
  hasError?: boolean;
  size: FieldIconSize;
};

const unitWidth = '48px';

const Wrapper = styled.div<FieldStyleProps>`
  ${FieldStyles}
  ${({ isFullWidth }) => isFullWidth && `width:100%;`}
`;

const InputWrapper = styled.div<InputWrapperStyleProps>`
  ${InputWrapperStyles}
  height: ${defaultInputHeight};
`;

const Label = styled(LabelComponent)<FieldLabelProps>`
  ${({ float, hasLeftIcon }) =>
    hasLeftIcon &&
    !float &&
    `
      left: ${theme.spacings[7].rem};
      padding-left: ${theme.spacings[1].rem};
  `}

  ${({ hasLeftUnit, isCompact }) =>
    hasLeftUnit &&
    isCompact &&
    `
      left: ${unitWidth};
      padding-left: ${theme.spacings[3].rem};
    `}
`;

const Input = styled.input<InputStyleProps & { hasLeftIcon: boolean }>`
  ${InputStyles}
  height: 48px;
  ${({ hasLeftIcon }) =>
    hasLeftIcon && `padding-left: ${theme.spacings[2].rem};`}
`;

const UnitStyles = css`
  align-items: center;
  background-color: ${theme.colors.neutrals.solidGrays[7]};
  box-sizing: border-box;
  display: flex;
  flex: 0 0 ${unitWidth};
  font-weight: 700;
  justify-content: center;
`;

const LeftUnitWrapper = styled(BodyText)`
  ${UnitStyles}
  border-right: 1px solid ${theme.colors.neutrals.solidGrays[5]};
  border-radius: 0 2px 2px 0;
`;

const RightUnitWrapper = styled(BodyText)`
  ${UnitStyles}
  border-left: 1px solid ${theme.colors.neutrals.solidGrays[5]};
  border-radius: 0 0 2px 2px;
`;

const IconWrapperStyles = css<IconProps>`
  align-items: center;
  display: flex;

  svg {
    fill: ${({ hasError }) =>
      hasError
        ? theme.colors.system.systemRed
        : theme.colors.primary.graysGray};
    width: ${({ size }) => (size === 'md' ? 22 : 28)}px;
  }
`;

const LeftIconWrapper = styled.div<
  IconProps & {
    hasFloatingLabel?: boolean;
  }
>`
  ${IconWrapperStyles}
  margin-left: ${theme.spacings[3].rem};
  transition: margin-top 0.2s;

  ${({ hasFloatingLabel }) =>
    hasFloatingLabel &&
    `
      margin-top: ${theme.spacings[4].rem};
    `}
`;

const RightIconWrapper = styled.div<IconProps>`
  ${IconWrapperStyles}
  margin-right: ${theme.spacings[3].rem};
`;

const IconWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

const StyledInfoCircleIcon = styled(InfoCircleIcon)`
  margin-left: ${({ theme }) => theme.spacings[1].rem};
  fill: ${({ theme }) => theme.colors.neutrals.text.tertiary};
`;

export const Field = React.forwardRef<HTMLInputElement, FieldProps>(
  (
    {
      alignUnit = 'left',
      errorMessage: _errorMessage = '',
      hasError,
      disabled = false,
      label,
      hasWrapperMarginBottom = true,
      iconLeft: IconLeft,
      iconRight: IconRight,
      iconSize = 'md',
      isCompact = false,
      isFullWidth = false,
      onChange,
      onClick,
      onBlur,
      onFocus,
      onKeyDown,
      placeholder,
      readOnly = false,
      required = false,
      size = 'md',
      type = 'text',
      unit,
      value = '',
      controlled = true,
      useParentState = false,
      hasTooltip,
      toolTipData = 'Tooltip Text',
      ...props
    },
    ref
  ) => {
    const [isFocussed, setIsFocussed] = useState(false);
    const [input, setInput] = useState(value);
    const isLabelFloating = floatLabel(isCompact, isFocussed, !input);
    const errorMessage = useMemo(() => _errorMessage.trim(), [_errorMessage])

    useEffect(() => {
      if (disabled) setIsFocussed(false);
    }, [disabled]);

    useEffect(() => {
      if (!useParentState) setInput(value);
    }, [useParentState, value]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      setInput(event.target.value);
      onChange && onChange(event);
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
      setIsFocussed(false);
      onBlur && onBlur(event);
    };

    const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
      setIsFocussed(true);
      onFocus && onFocus(event);
    };

    let valueToUse = {};
    if (controlled) {
      if (useParentState) {
        valueToUse = { value };
      } else {
        valueToUse = { value: input };
      }
    }


    return (
      <Wrapper
        haveMarginBottom={hasWrapperMarginBottom}
        isCompact={isCompact}
        isFullWidth={isFullWidth}
      >
        {label && (
          <Label
            hasLeftIcon={!!IconLeft}
            hasLeftUnit={alignUnit === 'left' && !!unit}
            float={isLabelFloating}
            isCompact={isCompact}
            required={required}
            value={label}
          />
        )}
        {hasTooltip && (
          <IconWrapper>
            <Tooltip
              customTrigger={<StyledInfoCircleIcon width={24} />}
              text={toolTipData}
            ></Tooltip>
          </IconWrapper>
        )}
        <InputWrapper
          disabled={disabled}
          hasError={!!errorMessage || !!hasError}
          isFocussed={isFocussed}
          readOnly={readOnly}
        >
          {unit && alignUnit === 'left' && (
            <LeftUnitWrapper>{unit}</LeftUnitWrapper>
          )}
          {IconLeft && (
            <LeftIconWrapper hasFloatingLabel={isLabelFloating} size={iconSize}>
              <IconLeft />
            </LeftIconWrapper>
          )}

          <Input
            ref={ref}
            disabled={disabled}
            hasLeftIcon={!!IconLeft}
            isCompact={isCompact}
            name={label}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onClick={onClick}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            readOnly={readOnly}
            showPlaceholder={!isCompact || isLabelFloating}
            type={type}
            textSize={size}
            {...valueToUse}
            {...props}
          />

          {(errorMessage || IconRight) && (
            <RightIconWrapper hasError={!!errorMessage} size={iconSize}>
              {errorMessage ? (
                <ExclamationTriangleIcon />
              ) : (
                IconRight && <IconRight />
              )}
            </RightIconWrapper>
          )}
          {unit && alignUnit === 'right' && (
            <RightUnitWrapper>{unit}</RightUnitWrapper>
          )}
        </InputWrapper>
        {errorMessage && <ErrorBox size="sm">{errorMessage}</ErrorBox>}
      </Wrapper>
    );
  }
);
